import { signRequestWithKey } from '../crypto/signing';
import type { SignedRequest } from '@shared/types';
import type { SigningKey } from '../crypto/ed25519Provider';
import { useAuthStore } from '../store/authStore';
import {
  canUseNativeMobilePushRuntime,
  clearNativeMobileRefreshGrant,
  loadNativeMobileRefreshGrantJson,
  loadNativeMobileServerTokenValidityJson,
  saveNativeMobileRefreshGrantJson,
  saveNativeMobileServerTokenValidityJson
} from '../utils/webviewBridge';

const MOBILE_REFRESH_GRANT_KEY = 'videocall.mobile_refresh_grant.v1';
const MOBILE_SERVER_TOKEN_VALIDITY_KEY = 'videocall.mobile_server_token_validity.v1';
const MOBILE_PUSH_REQUEST_TIMEOUT_MS = 15000;

export type StoredMobileRefreshGrant = {
  grantId: string;
  grantSecret: string;
  mobileEndpointId: string;
  centralBaseUrl: string;
  webOrigin?: string;
  savedAt: number;
};

export type MobileServerTokenValidity = Record<string, string>;
export type MobileDeliveryTokenRegistrationProgress = {
  stage: 'starting' | 'success' | 'error';
  deviceId: string;
  serverKey: string;
  completed: number;
  total: number;
  error?: string;
};

function normalizeUrlPrefix(url: string): string {
  return (url || '').trim().replace(/\/+$/, '');
}

function getServerKeyForDevice(deviceId: string): string {
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) throw new Error('Missing device');
  const serverUrl = state.identities[device.identityId]?.serverUrl;
  if (!serverUrl) throw new Error('Missing serverUrl');
  return normalizeUrlPrefix(serverUrl);
}

function getApiPrefixForDevice(deviceId: string): string {
  return `${getServerKeyForDevice(deviceId)}/api`;
}


async function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit, timeoutMs = MOBILE_PUSH_REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

export function getKnownServerKeys(): string[] {
  const state = useAuthStore.getState();
  const keys = new Set<string>();
  for (const device of Object.values(state.devices)) {
    const serverUrl = state.identities[device.identityId]?.serverUrl;
    const key = normalizeUrlPrefix(serverUrl || '');
    if (key) keys.add(key);
  }
  return Array.from(keys).sort();
}

async function registerMobileDeliveryToken(params: {
  deviceId: string;
  deviceSigningKey: SigningKey;
  mobileEndpointId: string;
  deliveryToken: string;
  deliveryTokenExpiresAt?: string;
  webOrigin?: string;
}): Promise<void> {
  const signedRequest: SignedRequest<{
    mobileEndpointId: string;
    deliveryToken: string;
    deliveryTokenExpiresAt?: string;
    webOrigin?: string;
  }> = await signRequestWithKey(
    'mobile:device:delivery-token:register',
    {
      mobileEndpointId: params.mobileEndpointId,
      deliveryToken: params.deliveryToken,
      ...(params.deliveryTokenExpiresAt ? { deliveryTokenExpiresAt: params.deliveryTokenExpiresAt } : {}),
      ...(params.webOrigin ? { webOrigin: params.webOrigin } : {})
    },
    params.deviceId,
    params.deviceSigningKey
  );

  const response = await fetchWithTimeout(`${getApiPrefixForDevice(params.deviceId)}/mobile/devices/delivery-tokens/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(signedRequest)
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to register delivery token');
  }
}

export async function unregisterMobileDeliveryForDevice(params: {
  deviceId: string;
  deviceSigningKey: SigningKey;
}): Promise<boolean> {
  try {
    const signedRequest: SignedRequest<Record<string, never>> = await signRequestWithKey(
      'mobile:device:delivery-token:unregister',
      {},
      params.deviceId,
      params.deviceSigningKey
    );
    const response = await fetchWithTimeout(`${getApiPrefixForDevice(params.deviceId)}/mobile/devices/bindings/unbind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signedRequest)
    }, 5000);
    const data = await response.json().catch(() => null);
    return response.ok && data?.success === true;
  } catch {
    return false;
  }
}

export async function registerMobileDeliveryTokenOnAllKnownServers(params: {
  mobileEndpointId: string;
  deliveryToken: string;
  deliveryTokenExpiresAt?: string;
  webOrigin?: string;
  onProgress?: (progress: MobileDeliveryTokenRegistrationProgress) => void;
}): Promise<{
  ok: Array<{ deviceId: string; serverKey: string }>;
  failed: Array<{ deviceId: string; serverKey: string; error: string }>;
}> {
  const state = useAuthStore.getState();
  const allDevices = Object.values(state.devices);
  const uniqueDevices = Array.from(
    new Map(allDevices.map((device) => [device.deviceId, device] as const)).values()
  );
  const total = uniqueDevices.length;

  const ok: Array<{ deviceId: string; serverKey: string }> = [];
  const failed: Array<{ deviceId: string; serverKey: string; error: string }> = [];
  let completed = 0;

  await Promise.all(
    uniqueDevices.map(async (device) => {
      const deviceId = device.deviceId;
      let serverKey = '';
      try {
        serverKey = getServerKeyForDevice(deviceId);
        params.onProgress?.({
          stage: 'starting',
          deviceId,
          serverKey,
          completed,
          total
        });
        await registerMobileDeliveryToken({
          deviceId,
          deviceSigningKey: device.signingKey,
          mobileEndpointId: params.mobileEndpointId,
          deliveryToken: params.deliveryToken,
          deliveryTokenExpiresAt: params.deliveryTokenExpiresAt,
          webOrigin: params.webOrigin
        });
        ok.push({ deviceId, serverKey });
        completed += 1;
        params.onProgress?.({
          stage: 'success',
          deviceId,
          serverKey,
          completed,
          total
        });
      } catch (e: any) {
        const error = String(e?.message || 'Failed to register');
        failed.push({ deviceId, serverKey, error });
        completed += 1;
        params.onProgress?.({
          stage: 'error',
          deviceId,
          serverKey,
          completed,
          total,
          error
        });
      }
    })
  );

  return { ok, failed };
}

export async function exchangeMobileHandoffViaCentral(params: {
  centralBaseUrl: string;
  handoffId: string;
  handoffCode: string;
}): Promise<{
  mobileEndpointId: string;
  deliveryToken: string;
  deliveryTokenExpiresAt: string;
  refreshGrantId?: string;
  refreshGrantSecret?: string;
}> {
  const base = normalizeUrlPrefix(params.centralBaseUrl);
  if (!base) throw new Error('Missing central base URL for handoff exchange');

  const response = await fetchWithTimeout(`${base}/api/mobile/notification-endpoints/handoffs/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoffId: params.handoffId, handoffCode: params.handoffCode })
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to exchange mobile handoff');
  }

  const mobileEndpointId = String(data.mobileEndpointId || '').trim();
  const deliveryToken = String(data.deliveryToken || '').trim();
  const deliveryTokenExpiresAt = String(data.deliveryTokenExpiresAt || '').trim();

  if (!mobileEndpointId || !deliveryToken) {
    throw new Error('Central exchange response missing token fields');
  }

  const refreshGrantId = String(data.refreshGrantId || '').trim();
  const refreshGrantSecret = String(data.refreshGrantSecret || '').trim();

  return {
    mobileEndpointId,
    deliveryToken,
    deliveryTokenExpiresAt,
    refreshGrantId: refreshGrantId || undefined,
    refreshGrantSecret: refreshGrantSecret || undefined,
  };
}

export async function fetchMobileHandoffStatusViaCentral(params: {
  centralBaseUrl: string;
  handoffId: string;
}): Promise<{
  status: string;
  mobileEndpointId?: string;
}> {
  const base = normalizeUrlPrefix(params.centralBaseUrl);
  if (!base) throw new Error('Missing central base URL for handoff status');

  const response = await fetchWithTimeout(
    `${base}/api/mobile/notification-endpoints/handoffs/${encodeURIComponent(params.handoffId)}`,
    undefined,
    8000
  );
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to fetch mobile handoff status');
  }

  return {
    status: String(data.status || '').trim() || 'unknown',
    mobileEndpointId: String(data.mobileEndpointId || '').trim() || undefined,
  };
}

export function saveMobileRefreshGrant(grant: StoredMobileRefreshGrant): void {
  if (saveNativeMobileRefreshGrantJson(JSON.stringify(grant))) {
    try {
      localStorage.removeItem(MOBILE_REFRESH_GRANT_KEY);
    } catch {
      // ignore storage errors
    }
    return;
  }
  try {
    localStorage.setItem(MOBILE_REFRESH_GRANT_KEY, JSON.stringify(grant));
  } catch {
    // ignore storage errors
  }
}

export function loadMobileRefreshGrant(): StoredMobileRefreshGrant | null {
  try {
    const nativeRaw = loadNativeMobileRefreshGrantJson();
    if (nativeRaw) {
      const parsed = JSON.parse(nativeRaw) as StoredMobileRefreshGrant;
      if (parsed?.grantId && parsed?.grantSecret && parsed?.mobileEndpointId && parsed?.centralBaseUrl) {
        return parsed;
      }
    }

    const raw = localStorage.getItem(MOBILE_REFRESH_GRANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredMobileRefreshGrant;
    if (!parsed?.grantId || !parsed?.grantSecret || !parsed?.mobileEndpointId || !parsed?.centralBaseUrl) {
      return null;
    }
    if (canUseNativeMobilePushRuntime() && saveNativeMobileRefreshGrantJson(JSON.stringify(parsed))) {
      localStorage.removeItem(MOBILE_REFRESH_GRANT_KEY);
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearMobileRefreshGrant(): void {
  clearNativeMobileRefreshGrant();
  try {
    localStorage.removeItem(MOBILE_REFRESH_GRANT_KEY);
  } catch {
    // ignore storage errors
  }
}

export async function redeemMobileRefreshGrantViaCentral(params: {
  centralBaseUrl: string;
  grantId: string;
  grantSecret: string;
}): Promise<{ mobileEndpointId: string; deliveryToken: string; deliveryTokenExpiresAt: string }> {
  const base = normalizeUrlPrefix(params.centralBaseUrl);
  if (!base) throw new Error('Missing central base URL for refresh-grant redeem');

  const response = await fetchWithTimeout(`${base}/api/mobile/notification-endpoints/refresh-grants/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ grantId: params.grantId, grantSecret: params.grantSecret })
  });

  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to redeem mobile refresh grant');
  }

  const mobileEndpointId = String(data.mobileEndpointId || '').trim();
  const deliveryToken = String(data.deliveryToken || '').trim();
  const deliveryTokenExpiresAt = String(data.deliveryTokenExpiresAt || '').trim();

  if (!mobileEndpointId || !deliveryToken) {
    throw new Error('Central redeem response missing token fields');
  }

  return { mobileEndpointId, deliveryToken, deliveryTokenExpiresAt };
}

function loadMobileServerTokenValidity(): MobileServerTokenValidity {
  try {
    const nativeRaw = loadNativeMobileServerTokenValidityJson();
    if (nativeRaw) {
      const nativeParsed = JSON.parse(nativeRaw) as MobileServerTokenValidity;
      if (nativeParsed && typeof nativeParsed === 'object') return nativeParsed;
    }

    const raw = localStorage.getItem(MOBILE_SERVER_TOKEN_VALIDITY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as MobileServerTokenValidity;
    if (!parsed || typeof parsed !== 'object') return {};
    if (canUseNativeMobilePushRuntime() && saveNativeMobileServerTokenValidityJson(JSON.stringify(parsed))) {
      localStorage.removeItem(MOBILE_SERVER_TOKEN_VALIDITY_KEY);
    }
    return parsed;
  } catch {
    return {};
  }
}

function saveMobileServerTokenValidity(data: MobileServerTokenValidity): void {
  if (saveNativeMobileServerTokenValidityJson(JSON.stringify(data))) {
    try {
      localStorage.removeItem(MOBILE_SERVER_TOKEN_VALIDITY_KEY);
    } catch {
      // ignore storage errors
    }
    return;
  }
  try {
    localStorage.setItem(MOBILE_SERVER_TOKEN_VALIDITY_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

export function updateMobileServerTokenValidity(serverKeys: string[], expiresAtIso: string): void {
  if (!expiresAtIso) return;
  const data = loadMobileServerTokenValidity();
  for (const key of serverKeys) {
    if (!key) continue;
    data[key] = expiresAtIso;
  }
  saveMobileServerTokenValidity(data);
}

export function shouldRefreshDeliveryTokenForKnownServers(params: {
  knownServerKeys: string[];
  thresholdMs?: number;
  nowMs?: number;
}): boolean {
  const threshold = Number.isFinite(params.thresholdMs) ? Number(params.thresholdMs) : 30 * 24 * 60 * 60 * 1000;
  const now = Number.isFinite(params.nowMs) ? Number(params.nowMs) : Date.now();
  const data = loadMobileServerTokenValidity();

  for (const key of params.knownServerKeys) {
    const expRaw = data[key];
    if (!expRaw) return true;
    const expMs = Date.parse(expRaw);
    if (!Number.isFinite(expMs)) return true;
    if (expMs - now < threshold) return true;
  }
  return false;
}
